/* Footer newsletter — subscribe without leaving the page, when Shopify allows it.
 *
 * The footer form is a Shopify customer form: it posts `form_type=customer` to
 * /contact and, on a store with the spam check on, Shopify's storefront-forms
 * provider owns the submit. The provider ends its flow with a real navigation,
 * which reloads the page and then restores the scroll to this field. Keeping the
 * shopper on the page therefore means submitting the token *ourselves*.
 *
 * What went wrong the first time (fixed 2026-09-21, see docs/finalisation/54-*):
 * this file used to work by replacing `form.submit` in the Liquid, before the
 * provider bound, so that the provider's end-of-flow call would land on our
 * fetch. But the provider does not only call `form.submit()` when the challenge
 * has been solved. Its submit listener runs this branch when a form it owns has
 * NO token yet:
 *
 *     const c = owns(form) && !token(form);
 *     if (c) { persistFormToSession(form); form.submit(); }
 *
 * — i.e. it deliberately submits before the token exists, as part of the
 * persist-then-resubmit path. Our interception caught that call, posted a form
 * with an empty `h-captcha-response`, and Shopify answered `Invalid CAPTCHA
 * token`. The old fallback then re-submitted natively *with the same token*, and
 * the shopper was navigated to /contact showing exactly that error.
 *
 * So the rule now is: we never submit without a token, and we never hijack the
 * provider.
 *
 *   1. On submit, if `h-captcha-response` already holds a token — the normal
 *      case, because the invisible challenge resolves shortly after the provider
 *      binds (as the form nears the screen, see whenNeeded) — prevent the
 *      navigation and post it with fetch,
 *      then report the result in place.
 *   2. If there is no token, we do nothing at all and let Shopify's own flow run.
 *      The shopper may get the reload, but they get subscribed, which is the
 *      behaviour they had before any of this existed.
 *
 * `window.Shopify.captcha.protect(form, cb)` is the supported way to ask for the
 * token, so we call it once — when the form nears the viewport or is touched,
 * never on page load (2026-10-09: that cost every page ~750 KB of hCaptcha) — to
 * make sure the widget is bound to *this* form rather than waiting for the
 * provider to find it.
 */
(function () {
  'use strict';

  var MARKERS = ['rl-footer__signup-msg--ok', 'customer_posted=true'];
  var BUSY = 'aria-busy';
  var TOKEN_FIELD = '[name="h-captcha-response"]';
  /* Long enough for a human to solve a challenge if Shopify demands one. */
  var CAPTCHA_WAIT_MS = 15000;
  var EMAIL_MSG = 'That email address was not accepted — please check it.';
  var CAPTCHA_MSG = 'We could not verify this browser — please try again.';

  function tokenIn(form) {
    var field = form && form.querySelector(TOKEN_FIELD);
    return field && typeof field.value === 'string' ? field.value : '';
  }

  /**
   * Did Shopify refuse the *token* rather than the address?
   *
   * These are different failures and the shopper can only act on the right one:
   * a bad address is theirs to fix, a token that came back "Invalid CAPTCHA
   * token" is Shopify declining a single-use token (usually because it was
   * already spent by Shopify's own flow). Reporting the first when the second
   * happened sends people to re-type a perfectly good address.
   */
  function refusedByCaptcha(text) {
    return /invalid captcha|captcha|hcaptcha|verify (that )?you/i.test(String(text || ''));
  }

  function clearToken(form) {
    var field = form && form.querySelector(TOKEN_FIELD);
    if (field) field.value = '';
  }

  function waitForToken(form, timeoutMs) {
    var deadline = Date.now() + timeoutMs;
    return new Promise(function (resolve) {
      (function poll() {
        if (tokenIn(form)) return resolve(true);
        if (Date.now() >= deadline) return resolve(false);
        window.setTimeout(poll, 400);
      })();
    });
  }

  /**
   * Get a fresh token from Shopify's own entry point.
   *
   * A token is single-use, so a rejected one has to be discarded before asking
   * again — and the asking is `Shopify.captcha.protect`, never a token-less
   * POST (which is exactly what Shopify answers with `Invalid CAPTCHA token`).
   */
  function requestFreshToken(form) {
    clearToken(form);
    try {
      if (window.Shopify && window.Shopify.captcha && window.Shopify.captcha.protect) {
        window.Shopify.captcha.protect(form, function () {});
      }
    } catch (e) { /* the wait below is the fallback */ }
    return waitForToken(form, CAPTCHA_WAIT_MS);
  }

  function message(form, text, ok) {
    if (!form) return;
    var el = form.querySelector('.rl-footer__signup-msg');
    if (!el) {
      el = document.createElement('small');
      el.className = 'rl-footer__signup-msg';
      form.appendChild(el);
    }
    el.classList.toggle('rl-footer__signup-msg--ok', !!ok);
    el.textContent = text;
  }

  function setBusy(form, busy) {
    if (!form) return;
    if (busy) form.setAttribute(BUSY, 'true');
    else form.removeAttribute(BUSY);
    var btn = form.querySelector('button[type="submit"]');
    if (btn) btn.disabled = !!busy;
  }

  function submitInPlace(form, attempt) {
    setBusy(form, true);
    var data = new FormData(form); // carries the hCaptcha token
    var url = form.getAttribute('action') || window.location.pathname;

    return fetch(url, {
      method: 'POST',
      body: data,
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    }).then(function (res) {
      return res.text().then(function (html) {
        var landed = res.url || '';
        var ok = res.ok && MARKERS.some(function (m) {
          return landed.indexOf(m) !== -1 || html.indexOf(m) !== -1;
        });

        if (ok) {
          setBusy(form, false);
          var field = form.querySelector('input[type="email"]');
          if (field) field.value = '';
          message(form, 'Thanks for subscribing.', true);
          return;
        }

        var captchaRefused = refusedByCaptcha(html) || refusedByCaptcha(landed);

        /* A spent token is worth exactly one retry with a fresh one: the address
           may be perfectly good and the shopper never saw a reason to change it.
           Still in place, still through Shopify's captcha. */
        if (captchaRefused && attempt < 1) {
          return requestFreshToken(form).then(function (ready) {
            if (ready) return submitInPlace(form, attempt + 1);
            setBusy(form, false);
            message(form, CAPTCHA_MSG, false);
          });
        }

        setBusy(form, false);
        /* Leave no spent token behind, so the shopper's next click re-asks
           rather than posting a token Shopify has already rejected. */
        if (captchaRefused) clearToken(form);
        message(form, captchaRefused ? CAPTCHA_MSG : EMAIL_MSG, false);
      });
    }).catch(function () {
      setBusy(form, false);
      message(form, 'Something went wrong — please try again.', false);
    });
  }

  function bind(form) {
    if (!form || form.dataset.rlNewsletterBound === 'true') return;
    /* Shopify's own challenge form (#captcha_form, data-nocaptcha) already has a
       checkbox widget; protect() on it renders a second hCaptcha and breaks both. */
    if (form.id === 'captcha_form' || form.dataset.nocaptcha !== undefined) return;
    form.dataset.rlNewsletterBound = 'true';

    form.addEventListener('submit', function (event) {
      /* No token? Then this is Shopify's flow to finish, not ours. Leaving the
         event alone is the whole point — see the note at the top of this file. */
      if (!tokenIn(form)) return;
      event.preventDefault();
      submitInPlace(form, 0);
    });

    /* Ask Shopify to bind its captcha once the form is close to the screen or
       touched, so a token is usually ready before the shopper has finished typing.
       Not on page load: binding pulls in about 750 KB of hCaptcha (including a
       proof-of-work script) on every page, which is most of the main-thread time
       a phone spends on the home page. Shopify's own provider still binds on
       focusin/change, so a form is never left unprotected. */
    whenNeeded(form, function () {
      try {
        if (window.Shopify && window.Shopify.captcha && window.Shopify.captcha.protect) {
          window.Shopify.captcha.protect(form, function () {});
        }
      } catch (e) { /* the provider's own listener still covers the form */ }
    });
  }

  /* Run `fn` once: when the form comes within ~a screen of the viewport, or on the
     first touch, pointer or focus inside it — whichever happens first. */
  function whenNeeded(form, fn) {
    var done = false;
    var io = null;
    var events = ['focusin', 'pointerdown', 'touchstart'];
    function go() {
      if (done) return;
      done = true;
      if (io) io.disconnect();
      events.forEach(function (name) { form.removeEventListener(name, go, true); });
      fn();
    }
    events.forEach(function (name) { form.addEventListener(name, go, { capture: true, passive: true }); });
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        if (entries.some(function (entry) { return entry.isIntersecting; })) go();
      }, { rootMargin: '0px 0px 600px 0px' });
      io.observe(form);
    } else {
      go();
    }
  }

  function boot() {
    var forms = document.querySelectorAll('form[action*="/contact"], .rl-footer__signup');
    for (var i = 0; i < forms.length; i++) bind(forms[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
  window.rlNewsletterBoot = boot;
})();
